import { InfoIcon } from "@chakra-ui/icons";
import {
  Box,
  Divider,
  Flex,
  FlexProps,
  Text,
  TooltipProps,
  useColorModeValue,
} from "@chakra-ui/react";
import { IconButton, Modal } from "components";
import React, { ReactNode, useState } from "react";
import { IconType } from "react-icons";
import { AiOutlineExpand } from "react-icons/ai";
import { Loader } from "../Loader";
import { Tooltip } from "../Tooltip";
import { Body } from "./Body";
import { LegacyCard } from "./legacy/Card";
import { byLook } from "theme/look";
import { CardColorScheme, CardVariant, tileBg, usePalette } from "./Card.theme";

export type CardProps = {
  title: string;
  titleTooltip?: string;
  titleTooltipPlacement?: TooltipProps["placement"];
  isLoading?: boolean;
  colorScheme?: CardColorScheme;
  /** `tile` is the filled KPI style; `panel` the bordered default. */
  variant?: CardVariant;
  actions?: ReactNode;
  hasData?: boolean;
  noDataMessage?: string;
  noDataIcon?: IconType;
  expandedSize?: React.ComponentProps<typeof Modal>["size"];
  isExpandable?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  children?: ReactNode | ((expanded: boolean) => ReactNode);
  ignoreTitleMargin?: boolean;
  minHeight?: FlexProps["minHeight"];
  minWidth?: FlexProps["minWidth"];
};

const BrandCard = ({
  title,
  titleTooltip,
  titleTooltipPlacement,
  children,
  colorScheme = "white",
  variant = "panel",
  isLoading = false,
  actions,
  hasData = true,
  noDataMessage = "No data found",
  noDataIcon,
  isExpandable = false,
  expandedSize,
  onExpandedChange,
  ignoreTitleMargin = false,
  minHeight = "150px",
  minWidth = "150px",
}: CardProps) => {
  const [isExpanded, setExpanded] = useState(false);
  const palette = usePalette(isLoading || hasData, colorScheme);
  const tileBackground = useColorModeValue(tileBg.light, tileBg.dark);
  const isTile = variant === "tile";

  return (
    <>
      <Modal
        size={expandedSize ?? "expandedCard"}
        isOpen={isExpanded}
        onClose={() => {
          setExpanded(false);
          onExpandedChange && onExpandedChange(false);
        }}
        title={title}
        body={
          <Body
            isExpanded={true}
            hasData={hasData}
            noDataIcon={noDataIcon}
            noDataMessage={noDataMessage}
            palette={palette}
          >
            {children}
          </Body>
        }
      />

      <Flex
        flexDir="column"
        bg={isTile ? tileBackground : palette?.bg}
        borderWidth="1px"
        borderStyle="solid"
        borderColor={isTile ? "transparent" : palette?.border}
        borderRadius="xl"
        minH={minHeight}
        minW={minWidth}
        w="100%"
      >
        {isLoading ? (
          <Loader stretch minHeight="100%" label="" />
        ) : (
          <>
            <Flex
              px={4}
              pt={4}
              flexDir="column"
              mb={ignoreTitleMargin ? undefined : 3}
            >
              {palette?.accent && (
                <Box
                  w="28px"
                  h="4px"
                  mb={3}
                  borderRadius="full"
                  bg={palette.accent}
                />
              )}
              <Text
                as="div"
                fontSize="xs"
                fontWeight="semibold"
                letterSpacing="0.06em"
                textTransform="uppercase"
                alignItems="center"
                display="flex"
                color={palette?.title}
                p={actions ? 0 : 1}
              >
                {title}
                <Flex flexGrow={1} />
                {titleTooltip && (
                  <Tooltip
                    aria-label={title}
                    placement={titleTooltipPlacement}
                    label={titleTooltip}
                  >
                    <InfoIcon color={palette?.helpIcon} w={3} h={3} ml={2} />
                  </Tooltip>
                )}

                {actions && <Flex ml={2}>{actions}</Flex>}

                {isExpandable && (
                  <Flex ml={2}>
                    <IconButton
                      size="xs"
                      variant="solid"
                      tooltip="Expand"
                      onClick={() => {
                        setExpanded(true);
                        onExpandedChange && onExpandedChange(true);
                      }}
                      icon={<AiOutlineExpand />}
                    />
                  </Flex>
                )}
              </Text>

              <Divider borderColor={palette?.divider} mt={1} mb={0} />
            </Flex>

            {!isExpanded && (
              <Body
                isExpanded={false}
                hasData={hasData}
                noDataIcon={noDataIcon}
                noDataMessage={noDataMessage}
                palette={palette}
              >
                {children}
              </Body>
            )}
          </>
        )}
      </Flex>
    </>
  );
};

const Card = byLook(BrandCard, LegacyCard);

export { Card };
